import type { IphoneSmsConversation } from '@casetrace/shared';
import { Paperclip } from 'lucide-react';
import { ServiceBadge } from './service-badge';

function formatDate(iso?: string): string | null {
  if (!iso) {
    return null;
  }
  return new Intl.DateTimeFormat('nb-NO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(iso));
}

function formatRange(conversation: IphoneSmsConversation): string {
  const from = formatDate(conversation.firstMessageAt);
  const to = formatDate(conversation.lastMessageAt);
  if (from && to) {
    return from === to ? from : `${from} – ${to}`;
  }
  return from ?? to ?? 'Ukjent dato';
}

type ConversationRowProps = {
  readonly conversation: IphoneSmsConversation;
  readonly checked: boolean;
  readonly onToggle: (chatId: string) => void;
};

export function ConversationRow({ conversation, checked, onToggle }: ConversationRowProps) {
  return (
    <label
      className="flex cursor-pointer items-start gap-2 rounded-md border border-white/[0.08] p-2.5 hover:bg-white/[0.03]"
      htmlFor={`conv-${conversation.chatId}`}
    >
      <input
        id={`conv-${conversation.chatId}`}
        type="checkbox"
        className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
        checked={checked}
        onChange={() => onToggle(conversation.chatId)}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium text-[#ededf0]">
            {conversation.displayName || conversation.chatIdentifier || 'Ukjent samtale'}
          </p>
          <ServiceBadge service={conversation.service} />
        </div>
        <p className="mt-0.5 text-xs text-[#5e6070]">{formatRange(conversation)}</p>
        <div className="mt-1 flex items-center gap-3 text-[11px] text-[#8b8d9a]">
          <span>{conversation.messageCount} meldinger</span>
          {conversation.attachmentCount > 0 && (
            <span className="inline-flex items-center gap-1">
              <Paperclip size={11} />
              {conversation.attachmentCount}
            </span>
          )}
        </div>
      </div>
    </label>
  );
}
